<template>
    <div>
        <div class="card shadow">
            <div class="card-header bg-transparent">
                <div>
                    <h3 class="mb-0">
                        {{ $tc('message.obstetricParameters', 2) }} {{prenatalConsultationInformation}}
                    </h3>
                </div>
            </div>

            <div class="card-body">
                <div class="card-title">
                    <router-link :to="{ name: 'add-parameter-obstetric' }" v-if="can">
                        {{ $t('message.add') }}

                        <i class="fas fa-plus-circle edit-icon"></i>
                    </router-link>
                </div>

                <template v-if="rows">
                    <b-container>
                        <b-row class="justify-content-sm-center">
                            <b-col col sm="6">
                                <b-form-group>
                                    <autocomplete
                                            ref="autocomplete"
                                            :placeholder="$t('message.search')"
                                            :source="mObstetricParameters"
                                            input-class="form-control"
                                            results-property="data"
                                            :results-display="formattedDisplay"
                                            @selected="itemSelected"
                                    ></autocomplete>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-container>

                    <b-table
                            id="ultrasounds-table"
                            primary-key="id"
                            :per-page="perPage"
                            :current-page="currentPage"
                            selectable
                            select-mode="single"
                            borderless
                            responsive
                            striped
                            hover
                            :sort-by.sync="sortBy"
                            :sort-desc.sync="sortDesc"
                            @row-selected="rowSelected"
                            :fields="fields"
                            :items="mObstetricParameters"
                    >
                        <!-- A virtual composite column for actions -->
                        <template slot="action" slot-scope="data">
                            <div class="row" v-if="data.item.isAuthor || hasAdminRole">
                                <div class="col-sm-6">
                                    <b-link :to="{ name: 'edit-parameter-obstetric', params: { consultation: consultation_id, parametreObstetrique: data.item.slug } }">
                                        <i class="fas fa-edit fa-2x edit-icon"></i>
                                    </b-link>
                                </div>

                                <div class="col-sm-6">
                                    <i v-b-modal="'delete-modal-' + data.item.slug" class="fas fa-trash fa-2x delete-icon"></i>
                                </div>
                            </div>

                            <b-modal
                                    :id="'delete-modal-' + data.item.slug"
                                    size="sm"
                                    header-bg-variant="danger"
                                    header-text-variant="light"
                                    :title="$t('message.delete') + ' ' + $tc('message.obstetricParameters', 1)"
                                    hide-footer
                            >
                                <div class="d-block text-center">
                                    <h3>{{ $t('message.delete') }} {{ data.item.weight }}?</h3>
                                </div>

                                <div class="d-block float-right">
                                    <b-button class="secondary" @click="deleteObstetricParameter(data.item.slug)">
                                        {{ $t('message.delete') }}
                                    </b-button>

                                    <b-button class="danger" @click="$bvModal.hide('delete-modal-' + data.item.slug)">
                                        {{ $t('message.cancel') }}
                                    </b-button>
                                </div>
                            </b-modal>
                        </template>
                    </b-table>

                    <b-pagination
                            class="justify-content-center"
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="perPage"
                            aria-controls="ultrasounds-table"
                    ></b-pagination>
                </template>

                <template v-else>
                    <div class="card">
                        <div class="card-body text-xs-center">
                            <p class="card-text">
                                {{ $t('message.noResults') }}
                            </p>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import _ from 'lodash';

    export default {
        name: "Obstetrics",

        computed: {
            ...mapGetters(["obstetricParameters","obstParameterPrenatalConsultation", "hasAdminRole", "hasMedecinControleRole", "hasPraticienRole","hasAssistante"]),

            can: function (){
                if (this.hasAdminRole){
                    return true;
                }else{
                    return this.hasMedecinControleRole || this.hasPraticienRole || this.hasAssistante;
                }
            },

            prenatalConsultationInformation:function(){
                return this.obstParameterPrenatalConsultation.type_de_consultation;
            },
            mObstetricParameters: function() {
                let uArray = [];
                let prenantalConsultation = this.obstParameterPrenatalConsultation;
                _.forEach(this.obstParameterPrenatalConsultation.parametres_obstetrique, function(obstetricParameter) {
                    let u = {};

                    u.taSystolique = obstetricParameter.ta_systolique;
                    u.heartNoise = obstetricParameter.bruit_du_coeur;
                    u.weight = obstetricParameter.poids;
                    u.touchVaginal = obstetricParameter.toucher_vaginal;
                    u.taDiastolique = obstetricParameter.ta_diastolique;
                    u.urineHeight = obstetricParameter.hauteur_urine;
                    u.id = obstetricParameter.id;
                    u.slug = obstetricParameter.slug;
                    u.prenatalSlug = prenantalConsultation.slug;

                    uArray.push(u);
                });

                return uArray;
            },

            rows: function() {
                return this.mObstetricParameters.length;
            }
        },
        data() {
            return {
                consultation_id: this.$route.params.consultation,
                perPage: 10,
                currentPage: 1,
                sortBy: 'date',
                sortDesc: true,
                fields: [
                    {
                        key: 'weight',
                        label: this.$i18n.t('message.weight'),
                        sortable: true
                    },
                    {
                        key: 'taSystolique',
                        label: this.$i18n.t('message.taSystolique'),
                        sortable: true
                    },
                    {
                        key: 'taDiastolique',
                        label: this.$i18n.t('message.taDiastolique'),
                        sortable: true
                    },
                    {
                        key: 'touchVaginal',
                        label: this.$i18n.t('message.touchVaginal'),
                        sortable: true
                    },
                    {
                        key: 'urineHeight',
                        label: this.$i18n.t('message.urineHeight'),
                        sortable: true
                    },
                    {
                        key: 'heartNoise',
                        label: this.$i18n.t('message.heartNoise'),
                        sortable: true
                    },
                    // {
                    //     key: 'date',
                    //     label: this.$i18n.t('message.createdAt'),
                    //     sortable: true
                    // },
                    {
                        key: 'action',
                        label: this.$i18n.tc('message.operations', 1)
                    }
                ],
                offset: 0,
                limit: -1,
            }
        },
        methods: {
            getObstetricParameters() {
                let args = {
                    consultation: this.consultation_id,
                    offset: this.offset,
                    limit: this.limit
                };

                this.$store.dispatch('getObstParameterPrenatalConsultation', { args : args });
            },

            formattedDisplay (result) {
                return result.weight;
            },

            itemSelected(item) {
                this.$router.push({ name: 'parameter-obstetric', params: { consultation: item.selectedObject.prenatalSlug,parametreObstetrique: item.selectedObject.slug } });
            },

            rowSelected(items) {
                this.$router.push({ name: 'parameter-obstetric', params: {consultation: items[0].prenatalSlug, parametreObstetrique: items[0].slug } });
            },

            deleteObstetricParameter(obstetricParameter) {
                let self = this;

                let args = {
                    parametreObstetrique: obstetricParameter
                };

                let successMessage = this.$i18n.t('message.obstetricParameterDeleted');
                let errorMessages = {
                    generic: this.$i18n.t('message.genericError'),
                    error: this.$i18n.t('message.error')
                };

                this.$store.dispatch('deleteObstetricParameter', { args, index: obstetricParameter, successMessage, errorMessages })
                    .then(() => {
                        self.$bvModal.hide('delete-modal-' + obstetricParameter);
                    });
            }
        },
        mounted() {
            // Fetch ultrasound list
            this.getObstetricParameters();
        }
    }
</script>

<style scoped>

</style>